import app from 'flarum/app';
import Modal from 'flarum/components/Modal';
import Select from 'flarum/components/Select';
import LoadingIndicator from '../components/LoadingIndicator';

const translationPrefix = 'kilowhat-formulaire.forum.export.';

/* global m */

const META_OPTIONS = [
    'uid',
    'user_id',
    'user_username',
    'user_displayname',
    'link_id',
    'locked_at',
    'created_at',
    'updated_at',
    'hidden_at',
];

const DEFAULT_META_OPTIONS = [
    'uid',
    'user_username',
    'created_at',
];

function numberedPre(content) {
    return m('.FormulaireExportModal-code', [
        m('pre.numbers', content.split('\n').map((line, index) => index + 1).join('\n')),
        m('pre.content', content),
    ]);
}

export default class ExportModal extends Modal {
    className() {
        return 'FormulaireModal FormulaireTheme FormulaireExportModal Modal--large';
    }

    title() {
        return app.translator.trans(translationPrefix + 'title');
    }

    init() {
        super.init();

        this.format = 'xlsx';
        this.heading = 'title';
        this.fields = null;
        this.meta = null;
        this.showAllMeta = false;
        this.loadingPreview = false;
        this.preview = null;

        this.updatePreview();
    }

    content() {
        const {form} = this.props;

        return m('.FormulaireExportModal-row', [
            m('.FormulaireExportModal-config.Modal-body', [
                m('.Form-group', m('a', {
                    className: 'Button Button--primary Button--block hasIcon',
                    download: form.title() + '.' + this.format,
                    href: this.generateUrl(),
                }, [
                    m('i.icon.fas.fa-file-download.Button-icon'),
                    m('span.Button-label', app.translator.trans(translationPrefix + 'submit')),
                ])),
                m('.Form-group', [
                    m('label', app.translator.trans(translationPrefix + 'format')),
                    Select.component({
                        value: this.format,
                        onchange: value => {
                            this.format = value;

                            this.updatePreview();
                        },
                        options: {
                            xlsx: app.translator.trans(translationPrefix + 'format-options.xlsx'),
                            xls: app.translator.trans(translationPrefix + 'format-options.xls'),
                            ods: app.translator.trans(translationPrefix + 'format-options.ods'),
                            csv: app.translator.trans(translationPrefix + 'format-options.csv'),
                            json: app.translator.trans(translationPrefix + 'format-options.json'),
                        },
                    }),
                ]),
                m('.Form-group', [
                    m('label', app.translator.trans(translationPrefix + 'heading')),
                    Select.component({
                        value: this.heading,
                        onchange: value => {
                            this.heading = value;

                            this.updatePreview();
                        },
                        options: {
                            title: app.translator.trans(translationPrefix + 'heading-options.title'),
                            key: app.translator.trans(translationPrefix + 'heading-options.key'),
                            none: app.translator.trans(translationPrefix + 'heading-options.none'),
                        },
                    }),
                ]),
                m('.Form-group', [
                    m('label', app.translator.trans(translationPrefix + 'fields')),
                    Array.isArray(form.template()) ? form.template().map(field => {
                        const checked = this.fields === null || this.fields.indexOf(field.key) !== -1;

                        return m('div', m('label', [
                            m('input', {
                                type: 'checkbox',
                                checked,
                                onchange: () => {
                                    if (this.fields === null) {
                                        this.fields = form.template().map(f => f.key);
                                    }

                                    if (checked) {
                                        this.fields = this.fields.filter(fk => fk !== field.key);
                                    } else {
                                        this.fields.push(field.key);
                                    }

                                    if (this.fields.length === form.template().length) {
                                        this.fields = null;
                                    }

                                    this.updatePreview();
                                },
                            }),
                            ' ',
                            field.title || field.key,
                        ]));
                    }) : null,
                ]),
                m('.Form-group', [
                    m('label', app.translator.trans(translationPrefix + 'meta')),
                    META_OPTIONS.map(meta => {
                        if (!this.showAllMeta && DEFAULT_META_OPTIONS.indexOf(meta) === -1) {
                            return null;
                        }

                        const checked = (this.meta === null && DEFAULT_META_OPTIONS.indexOf(meta) !== -1) ||
                            (Array.isArray(this.meta) && this.meta.indexOf(meta) !== -1);

                        return m('div', m('label', [
                            m('input', {
                                type: 'checkbox',
                                checked,
                                onchange: () => {
                                    if (this.meta === null) {
                                        this.meta = DEFAULT_META_OPTIONS.slice();
                                    }

                                    if (checked) {
                                        this.meta = this.meta.filter(thisMeta => thisMeta !== meta);
                                    } else {
                                        this.meta.push(meta);
                                    }

                                    if (
                                        this.meta.length === DEFAULT_META_OPTIONS.length &&
                                        this.meta.every(thisMeta => DEFAULT_META_OPTIONS.indexOf(thisMeta) !== -1)
                                    ) {
                                        this.meta = null;
                                    }

                                    this.updatePreview();
                                },
                            }),
                            ' ',
                            app.translator.trans(translationPrefix + 'meta-options.' + meta),
                        ]));
                    }),
                    this.showAllMeta ? null : m('div', m('a', {
                        href: '#',
                        onclick: event => {
                            event.preventDefault();
                            this.showAllMeta = true;
                        },
                    }, app.translator.trans(translationPrefix + 'more-meta'))),
                ]),
            ]),
            m('.FormulaireExportModal-preview', [
                m('h3', app.translator.trans(translationPrefix + 'preview')),
                this.viewPreview(),
            ]),
        ]);
    }

    viewPreview() {
        if (this.loadingPreview || !this.preview) {
            return LoadingIndicator.component({className: 'LoadingIndicator--block'});
        }

        if (this.preview.format === 'json') {
            return numberedPre(JSON.stringify(JSON.parse(this.preview.data), null, 2));
        } else if (this.preview.format === 'csv') {
            return numberedPre(this.preview.data);
        } else {
            const table = JSON.parse(this.preview.data);

            if (table.length) {
                return m('table', m('tbody', [
                    m('tr', [
                        m('th'),
                        table[0].map((value, index) => m('th', String.fromCharCode(65 + index))),
                    ]),
                    table.map((row, index) => m('tr', [
                        m('th', index + 1),
                        row.map(value => m('td', value)),
                    ])),
                ]));
            }

            return 'empty result set';
        }
    }

    generateUrl(preview = false) {
        const queryString = {};

        if (this.fields !== null) {
            queryString.fields = this.fields.join(',');
        }

        if (this.meta !== null) {
            queryString.meta = this.meta.join(',');
        }

        if (this.heading !== 'title') {
            queryString.heading = this.heading;
        }

        if (preview) {
            queryString.preview = 1;
        }

        const downloadQueryString = m.route.buildQueryString(queryString);

        return app.forum.attribute('apiUrl') + this.props.form.apiEndpoint() + '/export.' + this.format + (downloadQueryString ? '?' + downloadQueryString : '');
    }

    updatePreview() {
        this.loadingPreview = true;
        m.redraw(); // In Mithril 0.2, it seems the start/end computation of m.request causes the redraw to be delayed until the end of the request

        // We cache the value before the request
        // Because in case we click a lot of stuff, multiple requests could be pending
        const format = this.format;

        // Not using app.request because there's some odd stuff happening with deserializing
        m.request({
            method: 'GET',
            url: this.generateUrl(true),
            deserialize: raw => raw, // Do not automatically parse as JSON
        }).then(data => {
            this.loadingPreview = false;
            this.preview = {
                format,
                data,
            };
            m.redraw();
        }).catch(error => {
            this.loadingPreview = false;
            this.preview = null;
            m.redraw();

            throw error;
        });
    }
}
